import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  Paper,
  useTheme,
} from "@mui/material";
import { useEffect, useState } from "react";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";

export interface CreatePackPopupSubmitPayload {
  label: string;
  plannedQty: number;
  items: Array<{ clientSku: string; qty: number }>;
}

export type CreatePackPopupItem = {
  clientSku: string;
  productName?: string | null;
  title?: string | null;
};

interface CreatePackPopupProps {
  open: boolean;
  items: CreatePackPopupItem[];
  onClose: () => void;
  onSubmit: (payload: CreatePackPopupSubmitPayload) => void;
}

export default function CreatePackPopup({
  open,
  items,
  onClose,
  onSubmit,
}: CreatePackPopupProps) {
  const theme = useTheme();
  const [label, setLabel] = useState("");
  const [plannedQty, setPlannedQty] = useState("1");
  const [bomItems, setBomItems] = useState<CreatePackPopupItem[]>([]);
  const [itemQtys, setItemQtys] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setLabel("");
    setPlannedQty("1");
    setBomItems(items);
    const defaults: Record<string, string> = {};
    items.forEach((item) => {
      defaults[item.clientSku] = "1";
    });
    setItemQtys(defaults);
  }, [open, items]);

  const handleQtyChange = (clientSku: string, value: string) => {
    setItemQtys((current) => ({
      ...current,
      [clientSku]: value,
    }));
  };

  const handleRemoveItem = (clientSku: string) => {
    setBomItems((current) =>
      current.filter((item) => item.clientSku !== clientSku),
    );
    setItemQtys((current) => {
      const next = { ...current };
      delete next[clientSku];
      return next;
    });
  };

  const parsedPlannedQty = Number(plannedQty);
  const parsedItems = bomItems.map((item) => ({
    clientSku: item.clientSku,
    qty: Number(itemQtys[item.clientSku] || "0"),
  }));

  const isValid =
    label.trim().length > 0 &&
    Number.isFinite(parsedPlannedQty) &&
    parsedPlannedQty >= 0 &&
    bomItems.length > 0 &&
    parsedItems.every(
      (item) =>
        Number.isFinite(item.qty) && Number.isInteger(item.qty) && item.qty >= 1,
    );

  const handleSubmit = () => {
    if (!isValid) return;
    onSubmit({
      label: label.trim(),
      plannedQty: parsedPlannedQty,
      items: parsedItems,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box>
            <CustomTypography variant="heading4" fontWeight={700}>
              Create Pack
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Configure pack label, planned quantity, and BOM
            </CustomTypography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            overflow: "hidden",
          }}
        >
          <Grid2 container spacing={2} sx={{ flex: "0 0 auto" }}>
            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PACK LABEL
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    width="100%"
                    placeholder="Enter pack label"
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  PLANNED QTY
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Total packs planned for this configuration
                </CustomTypography>
                <Box sx={{ mt: 1 }}>
                  <CustomTextField
                    type="number"
                    value={plannedQty}
                    onChange={(e) => setPlannedQty(e.target.value)}
                    width="100%"
                    placeholder="Enter planned quantity"
                    allowNegativeValue={false}
                    allowDecimalValue={false}
                  />
                </Box>
              </Paper>
            </Grid2>

            <Grid2 size={12}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 3 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  BOM ITEMS ({bomItems.length})
                </CustomTypography>
                <CustomTypography variant="body3" color="text.secondary">
                  Qty is units of each SKU per pack. Remove items you do not
                  want in this pack.
                </CustomTypography>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={1.5}
                  sx={{
                    mt: 1.5,
                    maxHeight: bomItems.length > 3 ? 240 : "none",
                    overflowY: bomItems.length > 3 ? "auto" : "visible",
                    pr: bomItems.length > 3 ? 0.5 : 0,
                  }}
                >
                  {bomItems.length === 0 && (
                    <CustomTypography variant="body2" color="text.secondary">
                      No BOM items left. Close and select SKUs again to create a
                      pack.
                    </CustomTypography>
                  )}
                  {bomItems.map((item) => (
                    <Box
                      key={item.clientSku}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: theme.palette.customColors.white,
                        border: `1px solid ${theme.palette.customColors.greyBorder}`,
                      }}
                    >
                      <Box minWidth={0} flex={1}>
                        <CustomTypography variant="body2" fontWeight={600}>
                          {item.productName || item.title || "—"}
                        </CustomTypography>
                        <Box sx={{ mt: 0.25 }}>
                          <SkuCopyRow sku={item.clientSku} fontWeight={600} />
                        </Box>
                      </Box>
                      <Box sx={{ width: 110 }}>
                        <CustomTextField
                          type="number"
                          value={itemQtys[item.clientSku] || "1"}
                          onChange={(e) =>
                            handleQtyChange(item.clientSku, e.target.value)
                          }
                          width="100%"
                          placeholder="Qty"
                          allowNegativeValue={false}
                          allowDecimalValue={false}
                        />
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveItem(item.clientSku)}
                        aria-label={`Remove ${item.clientSku}`}
                        sx={{
                          color: theme.palette.customColors?.greyTextColor,
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid2>
          </Grid2>

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1.5}
            sx={{
              pt: 1,
              flex: "0 0 auto",
              borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              mt: "auto",
            }}
          >
            <AppButton label="Cancel" onClick={onClose} variant="grayscale" />
            <AppButton
              label="Create Pack"
              onClick={handleSubmit}
              variant="primary"
              disabled={!isValid}
            />
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
